import React, { useEffect, useRef, FC } from 'react'
import classNames from 'classnames'

import SALink from 'components/SALink'
import CaretDownIcon from 'svg/caret-down.svg'
import OpenNew from 'svg/SA/open-new-os.svg'

import { NavItem, isActiveNavItem } from '../helpers/constants'
import { handleStudioLinkClick } from '../helpers/navigate'
import { scrollIntoViewWhenReady } from '../helpers/scrollIntoViewWhenReady'
import styles from './NavMenu.module.scss'

type NavMenuProps = {
  title: string
  items: NavItem[]
  isOpen: boolean
  onToggle: () => void
  onNavigate: () => void
  onItemClick?: (event: React.MouseEvent<HTMLAnchorElement>, item: NavItem) => void
  activeHref?: string
}

const NavMenu: FC<NavMenuProps> = ({
  title,
  items,
  isOpen,
  onToggle,
  onNavigate,
  onItemClick,
  activeHref,
}) => {
  const navMenuRef = useRef<HTMLDivElement>(null)
  const hasHandledMountRef = useRef(false)

  // TODO: remove once Studio is rebuilt with all apps restructured to share one
  // persistent layout instance. Today each Studio app (artworks, promotions, sales, ...)
  // wraps itself in its own <NavigationLayout>, so navigating between pages backed by
  // different apps remounts this sidebar — a fresh DOM node always starts scrolled to
  // the top, even though the right section is already correctly marked open. Navigating
  // within the same app's page never hits this, since nothing gets remounted. Only run
  // this once, on that initial mount — not on every manual open/close toggle, which would
  // otherwise scroll the sidebar's own list on every click.
  useEffect(() => {
    if (hasHandledMountRef.current) {
      return
    }
    hasHandledMountRef.current = true
    if (!isOpen) {
      return
    }
    return scrollIntoViewWhenReady(navMenuRef.current, { block: 'nearest' })
  }, [isOpen])

  return (
    <div ref={navMenuRef} className={classNames(styles.navMenu, { [styles.navMenuOpen]: isOpen })}>
      <button type='button' className={styles.title} onClick={onToggle} aria-expanded={isOpen}>
        {title}
        <CaretDownIcon className={styles.caret} />
      </button>

      {isOpen && (
        <ul className={styles.items}>
          {items.map((item) => {
            const isActive = Boolean(activeHref && isActiveNavItem(activeHref, item))
            return (
              <li key={item.label}>
                <SALink
                  href={item.href}
                  disabled={item.isDisabled}
                  className={classNames(styles.item, {
                    [styles.itemActive]: isActive,
                    [styles.itemDisabled]: item.isDisabled,
                  })}
                  tabIndex={item.isDisabled ? -1 : 0}
                  target={item.openInNewTab ? '_blank' : ''}
                  rel={item.openInNewTab ? 'noopener noreferrer' : ''}
                  onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
                    if (!item.isDisabled) {
                      onItemClick?.(event, item)
                      if (item.label !== 'Log Out') {
                        handleStudioLinkClick(event, item)
                        onNavigate()
                      }
                    }
                  }}
                >
                  {item.label}
                  {item.showIcon && <OpenNew className={styles.icon} />}
                </SALink>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

export default NavMenu
